import sessionStore from '@/session/sessionStore'
import { CancellationReasonResponse } from '@/api/resources/common/cancellation-reason-services/cancellationReasonServices.models'
import {
    AssaysViewCaseResponse,
    AssaysViewSampleResponse
} from '@/components/assays-view/services/assays-view-case/assays-view-case.dto'
import { AssaysViewPortionResponse } from '@/components/assays-view/services/assays-view-portion/assays-view-portion.dto'
import { AssaysViewSubportionResponse } from '@/components/assays-view/services/assays-view-subportion/assays-view-subportion.dto'
import { AssayModalAssayWithValuableConceptResponse } from '@/components/assays-view/services/assays-view-assay/assays-view-assay.dto'
import { Nullable } from '@/shared/types/app.types'
import { CommonDeviceResponse } from '@/api/services/common/device/common-device.dto'
import { CommonAssayResponse } from '@/api/services/common/assay/common-assay.dto'
import { CommonPortionTypeResponse } from '@/api/services/common/portion-type/common-portion-type.dto'
import { CommonPriorityResponse } from '@/api/services/common/priority/common-priority.dto'
import { IncidentResponse } from '@/modules/incidentsModule/models/incidents.model'
import { LiquorTreeNode, LiquorTreeNodeStructure, LiquorTreeOptions } from 'liquor-tree'

// TODO move common enums and types to a shared location
// common enums
export enum IncidentEntityType {
    GENERAL = 'GENERAL',
    PATIENT = 'PATIENT',
    REQUEST = 'REQUEST',
    CASE = 'CASE',
    SAMPLE = 'SAMPLE',
    PORTION = 'PORTION',
    SUBPORTION = 'SUBPORTION'
}
export enum AttachmentEntityType {
    PATIENT = 'PATIENT',
    REQUEST = 'REQUEST',
    CASE = 'CASE',
    SAMPLE = 'SAMPLE',
    PORTION = 'PORTION',
    SUBPORTION = 'SUBPORTION',
    INCIDENT = 'INCIDENT',
    COMPLEMENTARY = 'COMPLEMENTARY',
    SYNOPTIC_TEMPLATE = 'SYNOPTIC_TEMPLATE',
    USERAPP = 'USERAPP',
    LABORATORY = 'LABORATORY',
    CASE_INTERNAL = 'CASE_INTERNAL',
    SHIPMENT = 'SHIPMENT'
}

// common types
export interface IncidentEntityIdHierarchy {
    casId?: number
    samId?: number
    porId?: number
    portionTypeEnum?: string
    supId?: number
}
interface ProtocolAssayIndexResponse {
    paaId: number,
    assay: CommonAssayResponse
}
export interface ProtocolIndexResponse {
    prtId: number,
    prtName: string,
    protocolAssays: ProtocolAssayIndexResponse[]
}

// common partially-defined types
interface PrivateIncidentResponse {
    incidentId: number
}
interface PrivateModule {
    moduleCode: string,
    activeStatus: boolean
}
interface PrivateLaboratoryConfiguration {
    activeModules: PrivateModule[]
}
interface PrivateSessionState {
    selectedLaboratoryConfiguration: PrivateLaboratoryConfiguration,
    priorities: any[]
}

// component types
export enum AssaysViewTypeEnum {
    CASE = 'case',
    SAMPLE = 'sample',
    PORTION = 'portion',
    SUBPORTION = 'subportion'
}
export interface LabeledEntity {
    label: string
}

export type AssaysViewSubportionNodeStructure = LiquorTreeNodeStructure<AssaysViewSubportionResponse>
export type AssaysViewPortionNodeStructure = LiquorTreeNodeStructure<AssaysViewPortionResponse, AssaysViewSubportionNodeStructure>
export type AssaysViewSampleNodeStructure = LiquorTreeNodeStructure<AssaysViewSampleResponse, AssaysViewPortionNodeStructure>
export type AssaysViewCaseNodeStructure = LiquorTreeNodeStructure<AssaysViewCaseResponse, AssaysViewSampleNodeStructure>

/* eslint-disable no-use-before-define */
export type AssaysViewSampleNode = LiquorTreeNode<AssaysViewSampleResponse, null, AssaysViewPortionNode>
export type AssaysViewPortionNode = LiquorTreeNode<AssaysViewPortionResponse, AssaysViewSampleNode, AssaysViewSubportionNode>
export type AssaysViewSubportionNode = LiquorTreeNode<AssaysViewSubportionResponse, AssaysViewPortionNode, never>
/* eslint-enable no-use-before-define */

export type AssaysViewNodeData = AssaysViewSampleResponse | AssaysViewPortionResponse | AssaysViewSubportionResponse
export type AssaysViewNode = AssaysViewSampleNode | AssaysViewPortionNode | AssaysViewSubportionNode

export interface AssaysViewModalState {
    caseNode: AssaysViewCaseNodeStructure | null
    treeOptions: LiquorTreeOptions
    selectedNodeForContextMenu: AssaysViewNode | null

    portionNumberOfCopies: number
    subportionNumberOfCopies: number
    duplicateNumberOfCopies: number

    loaded: boolean
    display: boolean

    samples: AssaysViewSampleResponse[]
    selectedSamples: AssaysViewSampleResponse[]

    portions: AssaysViewPortionResponse[]
    selectedPortions: AssaysViewPortionResponse[]
    portionsToSend: AssaysViewPortionResponse[]

    subportions: AssaysViewSubportionResponse[]
    selectedSubportions: AssaysViewSubportionResponse[]
    subportionsToSend: AssaysViewSubportionResponse[]

    subportionsPending: AssaysViewSubportionResponse[]
    subportionsProgress: AssaysViewSubportionResponse[]
    subportionsCompleted: AssaysViewSubportionResponse[]
    subportionsCanceled: AssaysViewSubportionResponse[]

    selectedCancellationReason: Nullable<CancellationReasonResponse>
    cancellationReasonsForPortions: CancellationReasonResponse[]
    cancellationReasonsForSubportions: CancellationReasonResponse[]

    sessionState: PrivateSessionState
    selectedPriority: Nullable<CommonPriorityResponse>

    portionTypes: CommonPortionTypeResponse[]
    selectedPortionType: Nullable<CommonPortionTypeResponse>

    assaysLoadLaboratoryIds: number[]
    assaysLoadLaboratoryIdsHasChanged: boolean
    assays: AssayModalAssayWithValuableConceptResponse[]
    selectedAssay: Nullable<AssayModalAssayWithValuableConceptResponse>

    devices: CommonDeviceResponse[]
    devicesLoading: boolean
    selectedDevice: Nullable<CommonDeviceResponse>

    assayViewModalSidepanel: boolean

    /// SIDE PANEL ///

    sidebarSample: Nullable<AssaysViewSampleResponse>
    sidebarPortion: Nullable<AssaysViewPortionResponse>
    sidebarSubportion: Nullable<AssaysViewSubportionResponse>

    incidents: IncidentResponse[]
    coreEntity: Nullable<IncidentEntityType>
    isIncidentSidepanel: boolean

    attachmentEntityType: Nullable<AttachmentEntityType>
    entitiesIdArray: number[]
    isAttachmentSidepanel: boolean
}
export const initialAssaysViewModalState = (): AssaysViewModalState => {
    return {
        caseNode: null,
        treeOptions: {
            checkbox: true,
            autoCheckChildren: false
        },
        selectedNodeForContextMenu: null,

        portionNumberOfCopies: 1,
        subportionNumberOfCopies: 1,
        duplicateNumberOfCopies: 1,

        loaded: false,
        display: true,

        samples: [],
        selectedSamples: [],

        portions: [],
        selectedPortions: [],
        portionsToSend: [],

        subportions: [],
        selectedSubportions: [],
        subportionsToSend: [],

        subportionsPending: [],
        subportionsProgress: [],
        subportionsCompleted: [],
        subportionsCanceled: [],

        cancellationReasonsForSubportions: [],
        cancellationReasonsForPortions: [],
        selectedCancellationReason: null,

        sessionState: sessionStore.state as unknown as PrivateSessionState,
        selectedPriority: null,

        portionTypes: [],
        selectedPortionType: null,

        assaysLoadLaboratoryIds: [],
        assaysLoadLaboratoryIdsHasChanged: false,
        assays: [],
        selectedAssay: null,

        devices: [],
        devicesLoading: false,
        selectedDevice: null,

        assayViewModalSidepanel: false,

        /// SIDE PANEL ///

        sidebarSample: null,
        sidebarPortion: null,
        sidebarSubportion: null,

        incidents: [],
        coreEntity: null,
        isIncidentSidepanel: false,

        attachmentEntityType: null,
        entitiesIdArray: [],
        isAttachmentSidepanel: false
    }
}
